import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CURRENCIES,
  ITEM_PRIORITIES,
  ITEM_PRIORITY_LABEL,
  type Currency,
  type ItemDto,
  type ItemPriority,
  type LinkPreviewResponse,
} from '@wishare/shared';
import { api, ApiError } from '../lib/api.js';
import { formatPrice, hostOf, minorToPriceInput, priceInputToMinor } from '../lib/format.js';
import { разобратьВставленнуюСтроку } from '../lib/card-from-page.js';
import { Badge, Button, ImageUploadButton, Notice, SelectField, TextAreaField, TextField } from './ui.js';
import { IconGift, IconGiftOutline, IconInfo, IconRepeat, IconWarning } from './icons.js';

/**
 * Форма желания — сердце продукта.
 *
 * Сценарий, ради которого всё затевалось: человек копирует ссылку откуда угодно
 * (маркетплейс, квартира в новостройке, безымянный магазин горнолыжных масок),
 * вставляет её сюда — и получает готовую карточку с точным фото и ценой.
 *
 * Три правила, определяющие поведение:
 *
 *  1. Разбор начинается сам, без нажатия кнопки. Вставил — увидел результат.
 *  2. Ничего не сохраняется автоматически. Мы показываем находку и ждём
 *     подтверждения: парсер может ошибиться, и последнее слово всегда за человеком.
 *  3. Любое поле правится руками, включая выбор фотографии из найденных.
 *     Если сайт не пустил нас вовсе — форма просто остаётся пустой и заполняется
 *     вручную, а ссылка всё равно сохраняется.
 */

export interface ItemFormValues {
  title: string;
  description: string;
  url: string | null;
  imageUrl: string | null;
  imageCandidates: string[];
  priceMinor: number | null;
  currency: Currency | null;
  priority: ItemPriority;
  quantity: number;
  groupGift: boolean;
  sourceSite: string | null;
}

interface ItemFormProps {
  /** Заполненные значения — когда редактируем существующее желание. */
  initial?: ItemDto | null;
  /**
   * Только ссылка, без остального. Нужно для приёма «поделиться»: человек кинул
   * ссылку из магазина, форма открывается с ней и сразу начинает разбор.
   */
  initialUrl?: string;
  /**
   * Готовая карточка, собранная не сервером, а браузером самого человека —
   * закладкой-кнопкой со страницы магазина.
   *
   * Нужна там, где сервер до страницы не доберётся: Ozon, DNS и им подобные
   * закрыты антиботом, и разбор по ссылке для них даёт в лучшем случае
   * название из адреса. Браузер человека эту страницу уже открыл законно,
   * поэтому данные приходят готовыми, и повторно дёргать разбор незачем.
   */
  initialCard?: PrefilledCard | null;
  onSubmit: (values: ItemFormValues) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
}

/** Карточка, собранная на стороне браузера. Все поля кроме адреса необязательны. */
export interface PrefilledCard {
  url: string;
  title?: string;
  description?: string;
  priceMinor?: number | null;
  currency?: Currency;
  imageUrl?: string;
  /**
   * Страница не похожа на карточку товара — скорее раздел или статья.
   *
   * Это не запрет: человек нажал кнопку осознанно, и наши признаки знают
   * не все страницы на свете. Но сказать об этом надо здесь, пока он смотрит
   * на поля, а не потом, когда обнаружит в списке желание с названием раздела.
   */
  uncertain?: boolean;
}

export function ItemForm({ initial, initialUrl, initialCard, onSubmit, onCancel, submitLabel = 'Добавить желание' }: ItemFormProps): JSX.Element {
  const [urlInput, setUrlInput] = useState(initial?.url ?? initialCard?.url ?? initialUrl ?? '');
  const [title, setTitle] = useState(initial?.title ?? initialCard?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? initialCard?.description ?? '');
  const [priceText, setPriceText] = useState(minorToPriceInput(initial?.priceMinor ?? initialCard?.priceMinor ?? undefined));
  const [currency, setCurrency] = useState<Currency>((initial?.currency as Currency) ?? initialCard?.currency ?? 'RUB');
  const [priority, setPriority] = useState<ItemPriority>(initial?.priority ?? 'normal');
  /*
   * Количество живёт в поле СТРОКОЙ, как и цена рядом.
   *
   * Держать его числом не выходит: во время набора поле законно бывает
   * пустым, а числу пустоту не выразить. Прежний вариант пересчитывал ввод
   * на каждое нажатие — и стереть единицу становилось нельзя вовсе, она
   * тут же возвращалась. Границы применяются на выходе из поля и при
   * отправке, см. `нормализоватьКоличество`.
   */
  const [quantityText, setQuantityText] = useState(String(initial?.quantity ?? 1));
  const [groupGift, setGroupGift] = useState(initial?.groupGift ?? false);

  const [images, setImages] = useState<string[]>(() => {
    const fromItem = initial?.images.map((image) => image.url) ?? [];
    const cover = initial?.imageUrl ?? initialCard?.imageUrl;
    return [...new Set([...(cover ? [cover] : []), ...fromItem])];
  });
  const [selectedImage, setSelectedImage] = useState<string | null>(initial?.imageUrl ?? initialCard?.imageUrl ?? null);
  const [customImage, setCustomImage] = useState('');
  const [showImagePicker, setShowImagePicker] = useState(false);

  const [preview, setPreview] = useState<LinkPreviewResponse | null>(null);
  /*
   * Повторы держим отдельным состоянием, а не читаем из разбора.
   *
   * Раньше они жили внутри ответа разбора — и вместе с ним пропадали для
   * карточек, принесённых закладкой: их не разбирают вовсе. Отдельное
   * состояние наполняется из обоих путей одинаково.
   */
  const [duplicates, setDuplicates] = useState<LinkPreviewResponse['duplicates']>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [titleTouched, setTitleTouched] = useState(Boolean(initial ?? initialCard?.title));

  /**
   * Адрес, который уже разобрали, — чтобы не дёргать сервер повторно.
   *
   * Для карточки из закладки сюда сразу кладётся её адрес. Иначе форма увидела
   * бы «новую» ссылку и отправила её на разбор, а разбор для закрытых магазинов
   * вернул бы пустоту и затёр бы то, что человек уже принёс со страницы.
   */
  /*
   * Карточку из закладки перепроверять не нужно — в ней уже есть цена и фото.
   * А вот карточка из системного «Поделиться» несёт одно название с телефона,
   * и разбор всё-таки запускаем: вдруг площадка нас пустит и добавит цену.
   * Название при этом не пострадает: пришедшее с телефона считается введённым
   * человеком и разбором не затирается.
   */
  /*
   * «Полная» — это цена И фотография, а не что-нибудь одно.
   *
   * Раньше хватало любого из двух, и это стало ловушкой ровно тогда, когда
   * приём «поделиться» научился привозить цену: карточка с телефона несла
   * название и цену, форма считала её полной и разбор не запускала — а вместе
   * с ним пропадала и фотография с площадок, которые нас пускают.
   */
  const карточкаУжеПолная = Boolean(initialCard?.priceMinor && initialCard?.imageUrl);
  const parsedUrlRef = useRef<string | null>(
    initial?.url ?? (карточкаУжеПолная ? initialCard?.url ?? null : null),
  );
  const parseSeq = useRef(0);

  /**
   * Что в поля положил САМ разбор прошлой ссылки.
   *
   * Нужно, чтобы отличить «тут лежит прежний товар» от «тут рука человека».
   * Сравнение по значению, а не по флажку «трогали поле»: флажок не отличает
   * набранное руками от положенного нами, а весь вопрос именно в этом.
   */
  const положеноРазбором = useRef<{
    title: string;
    description: string;
    priceText: string;
    selectedImage: string | null;
  }>({ title: '', description: '', priceText: '', selectedImage: null });

  /*
   * Затянулся ли разбор. Семь секунд — заметно больше обычного ответа
   * и заметно меньше того срока, на котором мы сдаёмся.
   */
  const [разборЗатянулся, setРазборЗатянулся] = useState(false);
  useEffect(() => {
    if (!isParsing) {
      setРазборЗатянулся(false);
      return undefined;
    }
    const таймер = setTimeout(() => setРазборЗатянулся(true), 7_000);
    return () => clearTimeout(таймер);
  }, [isParsing]);

  const trimmedUrl = urlInput.trim();
  const looksLikeUrl = useMemo(() => /^(https?:\/\/|[\w-]+\.[a-z]{2,})/i.test(trimmedUrl), [trimmedUrl]);

  /**
   * Автоматический разбор вставленной ссылки.
   *
   * Пауза в 600 мс нужна, чтобы не отправлять запрос на каждый набранный символ,
   * когда адрес вводят руками. При вставке из буфера пауза незаметна.
   */
  /*
   * Карточку, принесённую человеком, мы не разбираем — а предупреждение
   * о повторе приходило именно из разбора. Спрашиваем о нём отдельно:
   * это дешёвый вопрос к своей же базе, без похода в чужой сайт.
   */
  useEffect(() => {
    if (!initialCard?.url) return;
    let живо = true;
    void api.links
      .duplicates(initialCard.url)
      .then((ответ) => {
        if (живо && ответ.duplicates.length > 0) setDuplicates(ответ.duplicates);
      })
      .catch(() => undefined);
    return () => {
      живо = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!looksLikeUrl || trimmedUrl === parsedUrlRef.current) return undefined;

    const timer = setTimeout(() => {
      void runParse(trimmedUrl);
    }, 600);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedUrl, looksLikeUrl]);

  async function runParse(url: string, refresh = false): Promise<void> {
    const seq = ++parseSeq.current;
    setIsParsing(true);
    setParseError(null);

    try {
      const result = await api.links.preview(url, refresh);
      // Пока ждали ответ, пользователь мог вставить другую ссылку — старый
      // результат применять нельзя, иначе он затрёт новый.
      if (seq !== parseSeq.current) return;

      /*
       * «Сменилась» — именно другая ссылка, а не повтор той же.
       * «Обновить с сайта» зовёт разбор с тем же адресом, и там правило
       * замены не нужно: товар тот же, а правки человека — его.
       */
      const прежнийАдрес = parsedUrlRef.current;
      parsedUrlRef.current = url;
      setPreview(result);
      setDuplicates(result.duplicates);
      applyPreview(result, прежнийАдрес !== null && прежнийАдрес !== url);
    } catch (error) {
      if (seq !== parseSeq.current) return;
      setParseError(
        error instanceof ApiError
          ? error.message
          : 'Не получилось прочитать страницу. Заполните поля вручную — ссылка сохранится.',
      );
    } finally {
      if (seq === parseSeq.current) setIsParsing(false);
    }
  }

  /**
   * Переносит находки разбора в форму, не затирая то, что человек правил руками.
   *
   * Правило «заполняем только пустое» верно ровно один раз — для ПЕРВОЙ ссылки.
   * На второй оно оборачивалось поломкой: человек стирал ссылку, вставлял
   * другую, разбор честно приносил новый товар — а в форме оставались название,
   * фотография и описание прежнего, потому что поля уже не пустые. Получалось
   * желание-химера: ссылка на одно, название и картинка от другого, и заметить
   * это можно было только глазами.
   *
   * Поэтому помним, что в каждое поле положили МЫ САМИ (`положеноРазбором`).
   * Если ссылка сменилась и в поле лежит ровно наше прежнее значение — там не
   * рука человека, а прошлый товар, и его надо заменить. Заменить в том числе
   * ПУСТОТОЙ: новый разбор мог цены не найти, и оставлять цену от прежнего
   * товара — худшее из возможного, человек этого не заметит.
   *
   * Всё остальное — набранное руками, принесённое закладкой, выбранное
   * из «Поделиться» — не трогаем никогда.
   */
  function applyPreview(result: LinkPreviewResponse, ссылкаСменилась: boolean): void {
    const своё = положеноРазбором.current;
    const найденное = {
      title: result.title ?? '',
      description: result.description?.slice(0, 500) ?? '',
      priceText: result.priceMinor === null ? '' : minorToPriceInput(result.priceMinor),
    };

    /*
     * Значения читаем через обновление состояния, а не из замыкания: пока
     * ждали ответ сервера, человек мог что-то дописать, и замыкание помнит
     * форму такой, какой она была до его правки.
     */
    const наше = (текущее: string, положенное: string): boolean => ссылкаСменилась && текущее === положенное;

    setTitle((текущее) =>
      наше(текущее, своё.title) ? найденное.title : !titleTouched && найденное.title ? найденное.title : текущее,
    );
    setDescription((текущее) =>
      наше(текущее, своё.description) ? найденное.description : !текущее ? найденное.description : текущее,
    );
    setPriceText((текущее) =>
      наше(текущее, своё.priceText) ? найденное.priceText : !текущее ? найденное.priceText : текущее,
    );
    if (result.currency) setCurrency(result.currency);

    const found = [...new Set([result.imageUrl, ...result.images].filter((url): url is string => Boolean(url)))];
    const обложка = result.imageUrl ?? found[0] ?? null;
    if (found.length > 0) {
      setImages(found);
      setSelectedImage((current) =>
        current === null || (ссылкаСменилась && current === своё.selectedImage) ? обложка : current,
      );
    }

    положеноРазбором.current = {
      title: найденное.title,
      description: найденное.description,
      priceText: найденное.priceText,
      selectedImage: found.length > 0 ? обложка : своё.selectedImage,
    };
  }

  /*
   * Отличаем «магазин не пустил» от «мы ничего не нашли».
   *
   * Разница для человека огромная: в первом случае никакие правки ссылки
   * не помогут и нужен другой путь, во втором достаточно вписать поля.
   * Судим по тому, что сказал разбор, а не по пустоте полей: пустая карточка
   * бывает и у страницы, где просто нечего брать.
   */
  /*
   * Раньше отказ угадывался по словам в предупреждениях. Теперь разбор
   * говорит о нём прямо: догадка по тексту жила бы ровно до первой новой
   * формулировки, а формулировки эти пишем не мы.
   */
  const магазинНеПустил = Boolean(preview?.blockedBySite && !preview.priceMinor);

  /**
   * Приём того, чем поделились, прямо в поле ссылки.
   *
   * Кнопка «Поделиться» в приложении магазина отдаёт не голый адрес, а строку
   * вида «Куртка мужская Demix — Спортмастер https://sportmaster.ru/product/…».
   * Раньше такая вставка не разбиралась вовсе: форма ждала, что значение
   * НАЧИНАЕТСЯ с адреса, а здесь оно начинается с названия. Человек получал
   * пустую карточку и ссылку, склеенную с названием, — то есть худший исход
   * ровно в том случае, когда он всё сделал правильно.
   *
   * Теперь адрес достаётся из любого места строки, а остаток идёт в название.
   * Для площадок, закрытых от нашего сервера, это единственный путь, который
   * работает на любом телефоне: название приезжает от самого магазина,
   * ставить ничего не нужно.
   */
  function принятьВставленное(значение: string): void {
    const разобрано = разобратьВставленнуюСтроку(значение);
    if (!разобрано) {
      setUrlInput(значение);
      return;
    }

    setUrlInput(разобрано.адрес);

    // Название, пришедшее от магазина, считаем введённым человеком: разбор
    // его не затрёт, даже если сам вернётся с пустыми руками.
    if (разобрано.название && !titleTouched) {
      setTitle(разобрано.название);
      setTitleTouched(true);
    }

    /*
     * Цена из того, чем поделились. На закрытой площадке она единственная,
     * которую вообще можно узнать: до страницы наш сервер не доходит.
     *
     * Пустое поле не трогаем чужим значением только в одну сторону: если
     * человек уже что-то вписал, его цифра важнее присланной магазином.
     */
    if (разобрано.ценаМинор !== undefined && !priceText) {
      setPriceText(minorToPriceInput(разобрано.ценаМинор));
      if (разобрано.валюта && (CURRENCIES as readonly string[]).includes(разобрано.валюта)) {
        setCurrency(разобрано.валюта as Currency);
      }
    }
  }

  const canSubmit = title.trim().length > 0 && !saving;

  /** Дорогая вещь — та, на которую обычно скидываются, а не дарят в одиночку. */
  const expensive = (priceInputToMinor(priceText) ?? 0) >= 15_000_00;

  /*
   * Раскрывать ли «Подробности» сразу.
   *
   * Считается ОДИН РАЗ, при открытии формы, и дальше не пересчитывается:
   * иначе поставленная галочка складчины немедленно перерисовала бы раскладку
   * и та схлопнулась бы под пальцем в момент нажатия. Открываем, когда
   * уточнения у желания уже есть, — правя своё желание, человек обязан видеть
   * свой же комментарий, а не искать его под свёрнутым заголовком.
   */
  const [подробностиСразу] = useState(
    () =>
      Boolean(initial) &&
      Boolean(
        (initial?.description ?? '') !== '' ||
          (initial?.priority ?? 'normal') !== 'normal' ||
          (initial?.quantity ?? 1) > 1 ||
          initial?.groupGift,
      ),
  );

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setSaveError(null);

    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        url: trimmedUrl ? normalizeForSave(trimmedUrl) : null,
        imageUrl: selectedImage,
        imageCandidates: images.slice(0, 12),
        priceMinor: priceInputToMinor(priceText),
        currency: priceText ? currency : null,
        priority,
        quantity: нормализоватьКоличество(quantityText),
        groupGift,
        sourceSite: preview?.siteName ?? initial?.sourceSite ?? null,
      });
    } catch (error) {
      setSaveError(error instanceof ApiError ? error.message : 'Не удалось сохранить. Попробуйте ещё раз.');
      setSaving(false);
    }
  }

  /*
   * Значок находки. Зелёное «Нашли всё» МОЛЧИТ, когда рядом стоит оговорка.
   *
   * С живого прода: у Wildberries наш сервер не получает текущую цену, и под
   * карточкой честно написано «показана последняя известная, на 2 августа —
   * проверьте её на сайте». А сверху при этом горело зелёное «Нашли всё».
   * Читается сначала зелёное — и оговорку человек уже не читает: значок её
   * гасит. Хуже, чем отсутствие значка: он подписывается под тем, чего не
   * проверял, и ошибётся человек не в мелочи, а в цене подарка.
   *
   * Жёлтый и красный остаются: они оговорке не противоречат, а усиливают её.
   */
  const естьОговорки = preview !== null && preview.warnings.length > 0;
  const confidenceTone = !preview
    ? null
    : preview.confidence >= 0.75
      ? естьОговорки
        ? null
        : 'success'
      : preview.confidence >= 0.4
        ? 'warning'
        : 'danger';

  return (
    <form className="stack" onSubmit={handleSubmit}>
      {/* --- Шаг 1: ссылка --- */}
      <TextField
        label="Ссылка на желаемое"
        placeholder="Вставьте ссылку откуда угодно"
        value={urlInput}
        onChange={(event) => принятьВставленное(event.target.value)}
        onPaste={(event) => {
          /*
           * Читаем буфер сами, пока поле не съело переводы строк.
           *
           * Магазины делятся товаром в три строки: название, цена, ссылка —
           * так делает Авито. Однострочное поле ввода по правилам HTML
           * вычищает из вставленного текста переводы строк, и до нашего кода
           * доезжает «Велосипед Stels 12 000 ₽ https://…» одной строкой.
           * А различить в такой строке ценник рядом с названием и цену
           * ВНУТРИ названия («Подарочный сертификат 5000 ₽») нельзя никак:
           * они выглядят одинаково.
           *
           * В событии вставки текст ещё целый, со строками. Берём его оттуда,
           * а поле просим не вмешиваться.
           */
          const изБуфера = event.clipboardData?.getData('text') ?? '';
          if (!изБуфера.includes('\n')) return;
          event.preventDefault();
          принятьВставленное(изБуфера);
        }}
        inputMode="url"
        autoComplete="off"
        autoFocus={!initial}
        hint="Маркетплейс, квартира в новостройке, магазин горнолыжных масок — подойдёт что угодно. Можно и без ссылки."
      />

      {isParsing && (
        <div className="row" style={{ color: 'var(--text-muted)' }}>
          <span className="spinner" />
          <span className="small">
            Смотрим, что там на странице…
            {/*
              Через несколько секунд объясняем, почему так долго.
              Разбор ходит на чужой сайт и иногда поднимает настоящий браузер:
              полминуты там дело обычное. Но молчащая крутилка неотличима
              от зависшей — человек решает, что сломалось, и уходит. Та же
              беда, что и на входе в приложение, и лечится тем же.
            */}
            {разборЗатянулся && ' магазин отвечает медленно, ждём'}
          </span>
        </div>
      )}

      {parseError && <Notice tone="warning">{parseError}</Notice>}

      {preview && !isParsing && (
        <div className="row row--wrap" style={{ gap: 'var(--space-2)' }}>
          {preview.siteName && (
            <Badge tone="accent">
              {preview.faviconUrl && (
                // Сторонний сервис значков знает не все домены; при ошибке
                // прячем картинку, чтобы не показывать «битое фото».
                <img
                  className="item__favicon"
                  src={preview.faviconUrl}
                  alt=""
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
              )}
              {preview.siteName}
            </Badge>
          )}
          {confidenceTone && (
            <Badge tone={confidenceTone}>
              {preview.confidence >= 0.75
                ? 'Нашли всё'
                : preview.confidence >= 0.4
                  ? 'Нашли частично — проверьте'
                  : 'Почти ничего не нашли'}
            </Badge>
          )}
          {preview.cached && <Badge>из памяти</Badge>}
          <button
            type="button"
            className="link-button tiny"
            onClick={() => void runParse(trimmedUrl, true)}
            disabled={isParsing}
          >
            обновить с сайта
          </button>
        </div>
      )}

      {/*
        «Это у вас уже есть». Не запрещаем добавить повторно — иногда так и надо
        (две одинаковые вещи разным людям), — но предупреждаем: чаще это просто
        забывчивость, и человек будет рад узнать об этом до сохранения.
      */}
      {duplicates.length > 0 && (
        <Notice tone="warning" icon={<IconRepeat size={19} />}>
          <span className="small">
            Такая ссылка уже есть в{' '}
            {duplicates.length === 1 ? (
              <>
                списке «{duplicates[0]!.listTitle}» — «{duplicates[0]!.itemTitle}»
              </>
            ) : (
              <>{duplicates.length} ваших желаниях</>
            )}
            . Добавить всё равно можно.
          </span>
        </Notice>
      )}

      {preview && preview.warnings.length > 0 && (
        <Notice tone="warning" icon={<IconInfo size={19} />}>
          <ul style={{ margin: 0, paddingLeft: '1.1em' }}>
            {preview.warnings.map((warning) => (
              <li key={warning} className="small">
                {warning}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      {/*
        Карточку принесли со страницы, но страница не была карточкой товара.
        Говорим об этом прямо: разбор витрины или статьи выглядит удачным —
        название есть, цена есть, — и человек замечает подмену уже в списке,
        где вместо подарка стоит «Аксессуары» по цене случайного соседа.

        (Комментарий, стоявший здесь раньше, описывал не этот блок, а тот, что
        ниже, и вдобавок звал закладку, которой в нём больше нет.)
      */}
      {initialCard?.uncertain && (
        <Notice tone="warning">
          <span className="small">
            Похоже, это была не карточка товара, а раздел или статья. Проверьте название
            и цену — возможно, стоит открыть сам товар и нажать кнопку ещё раз.
          </span>
        </Notice>
      )}

      {/*
        Магазин нас не пустил — показываем не сожаление, а рабочий путь, по шагам.
        Фраза «попробуйте поделиться» без объяснения не работает: человек уже
        стоит перед пустой карточкой и не обязан догадываться, что именно
        нажать и куда потом деть результат. Поэтому шаги пронумерованы, и
        третий честно говорит, что делать, если Загадаря нет в списке «Поделиться»:
        этот запасной путь работает на любом телефоне, включая iPhone, где
        приём ссылок системой не поддерживается вовсе.
      */}
      {магазинНеПустил && (
        <Notice tone="info" icon={<IconGift size={19} />}>
          <div className="small stack stack--sm">
            <span>
              {hostOf(trimmedUrl) || 'Этот магазин'} не пускает наш сервер — так у них устроена
              защита, и с нашей стороны это не чинится. Зато вас он пускает: возьмите карточку
              своим телефоном.
            </span>
            <ol style={{ margin: 0, paddingLeft: '1.2em' }}>
              <li>Откройте товар в приложении магазина или в браузере телефона.</li>
              <li>Нажмите «Поделиться».</li>
              <li>
                Выберите «Загадарь». Если его нет в списке, нажмите «Копировать» и вставьте
                сюда, в поле ссылки, <b>целиком</b> — вместе с названием.
              </li>
            </ol>
            {/*
              Показываем, что именно вставлять, а не только просим вставить.
              Строка из «Поделиться» на вид мусорная — название, тире, имя
              магазина, адрес, — и человек честно пытается выбрать из неё одну
              ссылку. Пример снимает этот вопрос за секунду: видно, что вставлять
              надо ровно то, что дал магазин, и ничего не вычищать.
            */}
            <span style={{ color: 'var(--text-muted)' }}>
              Выглядит это так:{' '}
              <code style={{ wordBreak: 'break-all' }}>
                Куртка мужская Demix — Спортмастер https://www.sportmaster.ru/product/3031249029/
              </code>{' '}
              — вставляйте всё, как есть.
            </span>
            {/*
              Обещаем ровно то, что сбудется, и говорим, что делать с остальным.
              Название телефон присылает всегда, цену — когда магазин выносит её
              отдельной строкой (так делает Авито). А вот фотографии в тексте нет
              никогда, и вместо «придётся вписать руками» человеку полезнее
              знать, что кнопка для неё стоит прямо ниже: снимок экрана из
              приложения магазина годится.
            */}
            <span style={{ color: 'var(--text-muted)' }}>
              Название приедет с телефона само, а если магазин прислал и цену — приедет и она.
              Фотографию добавьте кнопкой «Загрузить своё фото» ниже: снимок экрана из приложения
              магазина вполне подойдёт.
            </span>
          </div>
        </Notice>
      )}

      {/* --- Шаг 3: то, что правит человек --- */}
      <TextField
        label="Что это"
        placeholder="Например, сноуборд Burton Custom 158"
        value={title}
        onChange={(event) => {
          setTitle(event.target.value);
          setTitleTouched(true);
        }}
        maxLength={300}
        required
      />

      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div className="grow">
          <TextField
            label="Цена"
            placeholder="—"
            value={priceText}
            onChange={(event) => setPriceText(event.target.value)}
            inputMode="decimal"
          />
        </div>
        <div style={{ width: 108 }}>
          <SelectField
            label="Валюта"
            value={currency}
            onChange={(value) => setCurrency(value as Currency)}
            options={CURRENCIES.map((code) => ({ value: code, label: code }))}
          />
        </div>
      </div>

      {/*
        Фотография стоит ПОСЛЕ названия и цены, а не перед ними.

        Раньше пустой квадрат с подарком встречал человека сразу под ссылкой:
        первое, что он видел в форме, — место для того, чего у него нет.
        Порядок теперь такой же, как в голове: что это, сколько стоит, и уже
        потом как выглядит. Разбор ссылки всё равно подставляет снимок сам,
        и человек видит его заполненным, а не просит заполнять.
      */}
      <div className="field">
        <span className="field__label">Фотография</span>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <ImagePreview url={selectedImage} />
          <div className="stack stack--sm grow">
            {images.length > 1 && (
              <button type="button" className="link-button small" onClick={() => setShowImagePicker((v) => !v)}>
                {showImagePicker ? 'Свернуть' : `Выбрать другую (${images.length})`}
              </button>
            )}
            <TextField
              placeholder="Или вставьте ссылку на картинку"
              value={customImage}
              onChange={(event) => setCustomImage(event.target.value)}
              onBlur={() => {
                const value = customImage.trim();
                if (!value) return;
                setImages((current) => [...new Set([value, ...current])]);
                setSelectedImage(value);
                setCustomImage('');
              }}
              inputMode="url"
            />
            {/* Запасной путь, когда сайт не дал фотографию: снять на камеру
                или выбрать из галереи телефона. */}
            <ImageUploadButton
              size="sm"
              label="Загрузить своё фото"
              onUploaded={(url) => {
                setImages((current) => [...new Set([url, ...current])]);
                setSelectedImage(url);
              }}
              onError={setParseError}
            />
          </div>
        </div>

        {showImagePicker && images.length > 1 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
              gap: 'var(--space-2)',
              marginTop: 'var(--space-2)',
            }}
          >
            {images.map((url) => (
              <button
                key={url}
                type="button"
                onClick={() => setSelectedImage(url)}
                aria-label="Выбрать эту фотографию"
                aria-pressed={selectedImage === url}
                style={{
                  aspectRatio: '1',
                  padding: 0,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-input)',
                  border: `2px solid ${selectedImage === url ? 'var(--accent)' : 'var(--border)'}`,
                }}
              >
                <img
                  src={url}
                  alt=""
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(event) => {
                    // Битую картинку убираем из выбора: показывать «сломанное фото»
                    // как вариант обложки бессмысленно.
                    event.currentTarget.closest('button')?.remove();
                  }}
                />
              </button>
            ))}
          </div>
        )}
      </div>


      {/*
        Подробности прячутся под раскладку.

        Всё, что ниже, — уточнения: важность, количество, складчина, коммент.
        Ни одно из них не обязательно, а вместе они занимали две трети окна:
        человек, вставивший ссылку, видел не «готово», а анкету из семи полей
        и листал её до кнопки. Теперь окно кончается на цене, а уточнения
        открываются одним нажатием — и открыты сразу, если в желании они уже
        есть (правка), иначе человек не нашёл бы собственный комментарий.

        `<details>`, а не своя раскладка на состоянии: браузер сам даёт ей
        клавиатуру, чтение с экрана и работу без JS.
      */}
      <details className="more" open={подробностиСразу}>
        <summary className="more__head">
          <span>Подробности</span>
          <span className="more__hint tiny muted">важность, количество, складчина, комментарий</span>
        </summary>

        <div className="more__body stack">
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div className="grow">
              <SelectField
                label="Насколько хочется"
                value={priority}
                onChange={(value) => setPriority(value as ItemPriority)}
                options={ITEM_PRIORITIES.map((value) => ({ value, label: ITEM_PRIORITY_LABEL[value] }))}
              />
            </div>
            <div style={{ width: 108 }}>
              <TextField
                label="Сколько"
                type="number"
                min={1}
                max={99}
                value={quantityText}
                onChange={(event) => setQuantityText(event.target.value)}
                onBlur={() => setQuantityText(String(нормализоватьКоличество(quantityText)))}
              />
            </div>
          </div>

          {/*
            Складчина. Предлагаем её заметно только для дорогих вещей: на кружку
            за 500 ₽ скидываться никто не станет, и лишний переключатель там только
            загромождает форму.
          */}
          <label
            className="row"
            style={{
              gap: 'var(--space-3)',
              alignItems: 'flex-start',
              cursor: 'pointer',
              padding: 'var(--space-3)',
              borderRadius: 'var(--radius-sm)',
              background: groupGift ? 'var(--accent-soft)' : 'transparent',
              border: `1px solid ${groupGift ? 'var(--accent)' : 'var(--border)'}`,
            }}
          >
            <input
              type="checkbox"
              checked={groupGift}
              onChange={(event) => setGroupGift(event.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, accentColor: 'var(--accent)' }}
            />
            <span>
              <span className="strong small">Можно скинуться вместе</span>
              <span className="tiny muted" style={{ display: 'block' }}>
                Друзья соберут сумму сообща, а не будут искать, кто потянет целиком.
                {expensive && ' Для такой цены это обычно удобнее.'}
              </span>
            </span>
          </label>

          <TextAreaField
            label="Комментарий"
            placeholder="Размер, цвет, где лучше купить — всё, что поможет дарителю"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={4000}
            rows={3}
          />
        </div>
      </details>

      {priceText && priceInputToMinor(priceText) !== null && (
        <p className="tiny faint">В карточке это будет выглядеть так: {formatPrice(priceInputToMinor(priceText), currency)}</p>
      )}

      {saveError && <Notice tone="danger">{saveError}</Notice>}

      {/*
        Кнопки прилипают к низу окна.

        Форма длинная: ссылка, фотография, название, цена, важность, складчина,
        комментарий. На телефоне подтверждение оказывалось ниже видимой части,
        и человеку приходилось догадываться, что нужно долистать до конца.
      */}
      <div className="form-actions">
        <Button variant="ghost" type="button" onClick={onCancel} block>
          Отмена
        </Button>
        <Button variant="primary" type="submit" disabled={!canSubmit} loading={saving} block>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function ImagePreview({ url }: { url: string | null }): JSX.Element {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [url]);

  return (
    <div
      style={{
        width: 104,
        height: 104,
        flexShrink: 0,
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
        background: 'var(--bg-input)',
        border: '1px solid var(--border)',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      {url && !failed ? (
        <img
          src={url}
          alt="Фото желания"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setFailed(true)}
        />
      ) : (
        /*
         * Значки рисованные, а не 🎁 и 🚫: эмодзи рисует система, и цветной
         * подарок посреди пустого квадрата читается как содержимое, хотя
         * означает ровно обратное — фотографии нет.
         */
        <span style={{ color: 'var(--text-faint)' }} aria-hidden="true">
          {failed ? <IconWarning size={30} /> : <IconGiftOutline size={34} />}
        </span>
      )}
    </div>
  );
}

/**
 * Количество штук — целое от 1 до 99. Приводим НА ВЫХОДЕ, а не на каждое нажатие.
 *
 * Разница не косметическая. Пока границы применялись прямо в `onChange`,
 * стереть значение было невозможно: пустое поле в тот же миг превращалось
 * обратно в «1», курсор вставал в конец, и следующая цифра приписывалась
 * к ней. Захар сказал об этом ровно так: «нельзя удалить 1 и поставить 5,
 * только дописать можно». Набор «12» при этом давал 99 — единица, к ней
 * приписанная единица, потом двойка, и потолок осаживал 112 до 99.
 *
 * Поэтому во время набора в поле лежит ровно то, что человек напечатал,
 * включая пустоту, а границы применяются при уходе из поля и при отправке.
 */
function нормализоватьКоличество(текст: string): number {
  const число = Math.trunc(Number(текст.trim()));
  if (!Number.isFinite(число) || число < 1) return 1;
  return Math.min(99, число);
}

/** Добавляем схему, если человек вставил адрес без неё: «ozon.ru/…» → «https://ozon.ru/…». */
function normalizeForSave(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return hostOf(`https://${url}`) ? `https://${url}` : url;
}
